import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { Container, Card, Row, Col, Button, Spinner } from "react-bootstrap";
import { CSVLink } from "react-csv";
import Header_Finance from "../../Components/Finance/Header_Finance";
import AppLoader from "../../Components/AppLoader";
import { BASE_URL } from "../../Config";
import "../Company/Company.css";

const PAYMENT_OPTIONS = ["Cash", "Wallet Payment", "Card"];

const emptyFilters = () => ({
  companyId: "",
  agentId: "",
  fromDate: "",
  toDate: "",
  month: "",
  year: "",
  mobileNumber: "",
  status: "",
  typeOfPayment: "",
});

const nullIfEmpty = (value) => {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed === "" ? undefined : trimmed;
};

/** Build body — omit empty fields (especially status). {} is valid. */
const buildFinanceViewBody = (filters) => {
  const body = {};

  const companyId = nullIfEmpty(filters.companyId);
  const agentId = nullIfEmpty(filters.agentId);
  const fromDate = nullIfEmpty(filters.fromDate);
  const toDate = nullIfEmpty(filters.toDate);
  const month = nullIfEmpty(filters.month);
  const year = nullIfEmpty(filters.year);
  const mobileNumber = nullIfEmpty(filters.mobileNumber);
  const status = nullIfEmpty(filters.status);
  const typeOfPayment = nullIfEmpty(filters.typeOfPayment);

  if (companyId) body.companyId = companyId;
  if (agentId) body.agentId = agentId;
  if (mobileNumber) body.mobileNumber = mobileNumber;
  if (status) body.status = status;
  if (typeOfPayment) body.typeOfPayment = typeOfPayment;

  // month/year take priority — ignore fromDate/toDate when month/year set
  if (month || year) {
    if (month) body.month = Number(month);
    if (year) body.year = Number(year);
  } else {
    if (fromDate) body.fromDate = fromDate;
    if (toDate) body.toDate = toDate;
  }

  return body;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const receiptSrc = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  if (raw.startsWith("/")) return `${BASE_URL.replace(/\/$/, "")}${raw}`;
  // assume base64 image without prefix
  if (raw.length > 64 && !raw.includes(" ")) {
    return `data:image/jpeg;base64,${raw}`;
  }
  return raw;
};

const Finance_RSA_Report = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [companies, setCompanies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [rows, setRows] = useState([]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - i);

  const loadCompanies = async () => {
    try {
      const response = await axios.get(`${BASE_URL}AllCompany?status=Active`);
      const list = Array.isArray(response.data?.companyDetails)
        ? response.data.companyDetails
        : [];
      // Prefer RSASALE companies in dropdown when productSale present
      const mapped = list
        .map((c) => ({
          id: c.applicationUserId || c.companyProfileId || c.userId || "",
          name: c.name || c.Name || c.companyName || "Company",
          productSale: String(c.productSale || c.ProductSale || "")
            .toUpperCase()
            .replace(/\s+/g, ""),
        }))
        .filter((c) => c.id && c.productSale === "RSASALE");
      setCompanies(mapped);
    } catch {
      setCompanies([]);
    }
  };

  const loadAgents = async (companyId) => {
    if (!companyId) {
      setAgents([]);
      return;
    }
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Agents?userId=${companyId}`,
        { headers: { accept: "*/*" } }
      );
      if (
        response.data?.responseCode === "0000" &&
        Array.isArray(response.data.agents)
      ) {
        setAgents(response.data.agents);
      } else {
        setAgents([]);
      }
    } catch {
      setAgents([]);
    }
  };

  const fetchReport = useCallback(async (filterValues = filters) => {
    setLoading(true);
    try {
      const body = buildFinanceViewBody(filterValues);
      const response = await axios.post(
        `${BASE_URL}RetailApp_FinanceView`,
        body,
        { headers: { "Content-Type": "application/json" } }
      );

      const res = response.data || {};
      const code = String(res.responseCode ?? res.ResponseCode ?? "").trim();

      if (code === "0001") {
        Swal.fire("Error", res.message || "Request failed.", "error");
        setRows([]);
        setTotalRecords(0);
        setTotalAmount(0);
        return;
      }

      if (code && code !== "0000") {
        Swal.fire("Info", res.message || "Unexpected response.", "info");
        setRows([]);
        setTotalRecords(0);
        setTotalAmount(0);
        return;
      }

      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalRecords(Number(res.totalRecords ?? data.length ?? 0));
      setTotalAmount(Number(res.totalAmount ?? 0));
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load RSA finance report.", "error");
      setRows([]);
      setTotalRecords(0);
      setTotalAmount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const stored = sessionStorage.getItem("user");
    if (!stored) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }
    const user = JSON.parse(stored);
    if (user.userRole !== "Finance") {
      Swal.fire("Error", "Finance access only.", "error");
      return;
    }

    loadCompanies();
    // Default: empty {} — all RSASALE sales (no today default)
    fetchReport(emptyFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "companyId") {
        next.agentId = "";
        loadAgents(value);
      }
      // If month/year used, clear date range UI optional — keep both, body ignores dates
      return next;
    });
  };

  const handleApply = () => fetchReport(filters);

  const handleReset = () => {
    // Clear filters only — do not call API (user preference from earlier)
    setFilters(emptyFilters());
    setAgents([]);
  };

  const handleClearAndLoadAll = async () => {
    const cleared = emptyFilters();
    setFilters(cleared);
    setAgents([]);
    await fetchReport(cleared);
  };

  const openReceipt = (row) => {
    const src = receiptSrc(row.imageRecipt);
    if (!src) {
      Swal.fire("Info", "No receipt image available.", "info");
      return;
    }
    Swal.fire({
      title: "Receipt",
      imageUrl: src,
      imageAlt: "Receipt",
      width: 560,
      confirmButtonText: "Close",
    });
  };

  const columns = [
    {
      name: "Sale Date",
      selector: (row) => formatDate(row.saleDateTime),
      sortable: true,
      grow: 1.2,
      wrap: true,
    },
    {
      name: "Customer",
      selector: (row) => row.customerName || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.customerMobileNumber || "-",
      sortable: true,
      width: "140px",
      wrap: true,
      allowOverflow: true,
    },
    {
      name: "CNIC",
      selector: (row) => row.customerCnic || "-",
      sortable: true,
      width: "160px",
      wrap: true,
      allowOverflow: true,
    },
    {
      name: "Gender",
      selector: (row) => row.customerGender || "-",
      width: "90px",
    },
    {
      name: "Package",
      selector: (row) => row.packageName || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Amount",
      selector: (row) => `PKR ${formatMoney(row.amount)}`,
      sortable: true,
      width: "110px",
    },
    {
      name: "Mode of Sale",
      selector: (row) => row.modeOfSale || "-",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "-",
      sortable: true,
      width: "100px",
    },
    {
      name: "Receipt",
      cell: (row) =>
        receiptSrc(row.imageRecipt) ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openReceipt(row)}
          >
            View
          </button>
        ) : (
          <span className="text-muted">-</span>
        ),
      ignoreRowClick: true,
      width: "90px",
    },
    {
      name: "Agent",
      selector: (row) => row.agentName || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Company",
      selector: (row) => row.companyName || "-",
      sortable: true,
      wrap: true,
    },
  ];

  const csvData = rows.map((row) => ({
    SaleDate: row.saleDateTime ?? "",
    Customer: row.customerName ?? "",
    Mobile: row.customerMobileNumber ?? "",
    CNIC: row.customerCnic ?? "",
    Gender: row.customerGender ?? "",
    Package: row.packageName ?? "",
    Amount: row.amount ?? "",
    ModeOfSale: row.modeOfSale ?? "",
    Status: row.status ?? "",
    Agent: row.agentName ?? "",
    Company: row.companyName ?? "",
    ProductSale: row.productSale ?? "RSASALE",
  }));

  return (
    <div className="co-page co-page--rsasale">
      <Header_Finance rsaMode />

      <Container fluid="xl">
        <div className="co-hero d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h2>
              <i className="bi bi-clipboard2-data me-2" />
              RSA Sales Report
            </h2>
            <p>Complete RSASALE sales listing (Finance View)</p>
          </div>
          <span className="co-hero-badge">
            <i className="bi bi-patch-check-fill" />
            RSASALE
          </span>
        </div>

        <Card className={`co-card${loading ? " co-card-loading" : ""}`}>
          {loading && (
            <AppLoader rsa overlay message="Loading RSA sales report..." />
          )}

          <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
            <div>
              <h5 className="mb-0">
                <i className="bi bi-funnel me-2" style={{ color: "#f58123" }} />
                Filters
              </h5>
              <small className="text-muted">
                Empty filters load all RSASALE sales
              </small>
            </div>
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <span className="badge text-bg-light border">
                Records: <strong>{totalRecords}</strong>
              </span>
              <span className="badge text-bg-light border">
                Total Amount: <strong>PKR {formatMoney(totalAmount)}</strong>
              </span>
            </div>
          </Card.Header>

          <Card.Body>
            <div className="co-filter-bar mb-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Company</label>
                  <select
                    className="form-select"
                    value={filters.companyId}
                    onChange={(e) => handleFilterChange("companyId", e.target.value)}
                  >
                    <option value="">All</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.productSale ? ` (${c.productSale})` : ""}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Agent</label>
                  <select
                    className="form-select"
                    value={filters.agentId}
                    onChange={(e) => handleFilterChange("agentId", e.target.value)}
                    disabled={!filters.companyId}
                  >
                    <option value="">All</option>
                    {agents.map((a) => (
                      <option
                        key={a.agentId || a.userId}
                        value={a.agentId || a.userId}
                      >
                        {a.name || a.agentName || a.email || "Agent"}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                  />
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange("toDate", e.target.value)}
                  />
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Month</label>
                  <select
                    className="form-select"
                    value={filters.month}
                    onChange={(e) => handleFilterChange("month", e.target.value)}
                  >
                    <option value="">All</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1)}>
                        {new Date(2000, i, 1).toLocaleString("en", {
                          month: "short",
                        })}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Year</label>
                  <select
                    className="form-select"
                    value={filters.year}
                    onChange={(e) => handleFilterChange("year", e.target.value)}
                  >
                    <option value="">All</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Mobile</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="03xx..."
                    value={filters.mobileNumber}
                    onChange={(e) =>
                      handleFilterChange("mobileNumber", e.target.value)
                    }
                  />
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">
                    Payment Type
                  </label>
                  <select
                    className="form-select"
                    value={filters.typeOfPayment}
                    onChange={(e) =>
                      handleFilterChange("typeOfPayment", e.target.value)
                    }
                  >
                    <option value="">All</option>
                    {PAYMENT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={handleApply}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <i className="bi bi-search me-1" />
                        Apply
                      </>
                    )}
                  </Button>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={handleClearAndLoadAll}
                    disabled={loading}
                  >
                    All Sales
                  </Button>
                </Col>

                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="outline-danger"
                    className="w-100"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="d-flex flex-wrap gap-3">
                <span>
                  <strong>{totalRecords}</strong> record
                  {totalRecords === 1 ? "" : "s"}
                </span>
                <span>
                  Total Amount: <strong>PKR {formatMoney(totalAmount)}</strong>
                </span>
              </div>
              <CSVLink
                data={csvData}
                filename="rsa_finance_sales_report.csv"
                className={`btn btn-sm btn-success ${
                  rows.length === 0 ? "disabled" : ""
                }`}
              >
                <i className="bi bi-download me-1" />
                Download CSV
              </CSVLink>
            </div>

            <div className="table-responsive">
              <DataTable
                columns={columns}
                data={rows}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 15, 20, 50, 100]}
                highlightOnHover
                responsive
                dense
                striped
                progressPending={false}
                noDataComponent={
                  <div className="py-4 text-muted">
                    <i className="bi bi-inbox fs-3 d-block mb-2" />
                    No RSA sales records
                  </div>
                }
              />
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Finance_RSA_Report;
